import type { UpcomingTickets } from '@cobliteam/api-lib/src/__generated__/graphql';
import { TicketStatus } from '@cobliteam/common-types';
import { completionDataQueryOptions, useCompletionData } from '@cobliteam/hooks/src/useEntity/useCompletionData';
import { upcomingTicketsQueryOptions } from '@cobliteam/hooks/src/useEntity/useUpcomingTickets';
import useUser from '@cobliteam/hooks/src/useEntity/useUser';
import useFeatureFlags from '@cobliteam/hooks/src/useFeatureFlags';
import { usePermissions } from '@cobliteam/permissions';
import { useQueryClient } from '@tanstack/react-query';
import { type SchedulingAgentEntryPoint, useSchedulingAgent } from './useSchedulingAgent';

// Mesmo staleTime padrão do client: o clique reaproveita o que a home ou um prefetch já trouxe.
const ON_DEMAND_STALE_TIME = 5 * 60 * 1000;

type UsePendingInstallSchedulingArgs = {
  /**
   * Busca a contagem de instalações pendentes no render. Só o nudge precisa dela para decidir se
   * aparece; o botão flutuante só precisa da permissão e busca o resto no clique.
   */
  fetchPendingCount?: boolean;
};

const findSingleTicketId = (pendingCount: number, upcomingTickets: UpcomingTickets | null | undefined) => {
  const pendingTickets = (upcomingTickets?.tickets ?? []).filter((ticket) => ticket?.type === 'SCHEDULING' && ticket.status === TicketStatus.SCHEDULE_INSTALLATION);
  /*
   * `pendingCount` is an aggregate (`openTicketsToInstall`) and `pendingTickets` comes from the
   * upcoming list — different queries. Scoping the agent to one ticket while the copy announces
   * three would open a conversation about the wrong thing, so both sides have to agree on "one".
   */
  return pendingCount === 1 && pendingTickets.length === 1 ? pendingTickets[0]?.id : undefined;
};

/**
 * "Can we offer installation scheduling, and how do we start it?" — one owner for both surfaces
 * that ask: the nudge in the floating stack and the option in the floating action button.
 *
 * Both live in the app shell, so the render only reads what it needs to decide what to show: the
 * CRM permission (free) and, for the nudge, the `completionData` count. The ticket list
 * (`upcomingTickets`) is only needed to scope the agent, so it is fetched on click (or prefetched
 * when the FAB menu opens), sharing the react-query key with the home installation card.
 */
export const usePendingInstallScheduling = ({ fetchPendingCount = true }: UsePendingInstallSchedulingArgs = {}) => {
  const { isAccessAllowed } = usePermissions();
  const { ordersAiPage } = useFeatureFlags();
  const { getSession } = useUser();
  const fleetId = getSession()?.fleet?.id ?? '';
  const agent = useSchedulingAgent();
  const queryClient = useQueryClient();

  const hasCrmPermission = isAccessAllowed('create-update-delete:crm');

  /*
   * Gated on the permission, which is free, BEFORE the query — this hook is mounted in the app
   * shell, so an ungated call would fetch on every page for every fleet. `refetchOnWindowFocus:
   * false`: a passive suggestion should not drive a refetch every time the user comes back to the tab.
   */
  const { data: completionData } = useCompletionData({ options: { enabled: hasCrmPermission && fetchPendingCount, refetchOnWindowFocus: false } });
  const pendingCount = completionData?.openTicketsToInstall ?? 0;

  const fetchOnDemand = <T>(options: { queryKey: unknown[]; queryFn: () => Promise<T> }) => queryClient.fetchQuery({ ...options, staleTime: ON_DEMAND_STALE_TIME });

  return {
    /*
     * Two different questions, deliberately separate — they used to share one `isAvailable` flag,
     * which forced the FAB entry point and the floating nudge to appear together.
     */
    /** May this user reach the scheduling agent at all? Entry points (the FAB option) use this. */
    canOpenAgent: hasCrmPermission,
    /** Is there something concrete to schedule? The proactive nudge uses this — announcing a count
     *  of zero pending installations would be a card with nothing to say. */
    hasPendingInstalls: hasCrmPermission && pendingCount > 0,
    pendingCount,
    /** Antecipa a lista de tickets (ex.: ao abrir o menu do botão), para o clique não esperar a rede. */
    prefetch: () => {
      if (!hasCrmPermission || ordersAiPage) return;
      void queryClient.prefetchQuery({ ...upcomingTicketsQueryOptions(fleetId), staleTime: ON_DEMAND_STALE_TIME });
    },
    /*
     * Com a flag `orders-ai-page`, o FAB leva à apresentação do agente em vez de já mandar um
     * prompt: a página mostra tudo o que ele faz, e escolher o assunto pelo usuário desperdiça
     * justamente a tela que o experimento quer testar.
     *
     * Sem a flag nada muda — o painel flutuante não tem tela de apresentação, então abrir sem
     * mensagem deixaria a pessoa diante de um chat vazio.
     *
     * One pending ticket: the agent opens already scoped to it. Otherwise it starts from the fleet.
     * Resolves to whether the agent opened scoped to a ticket.
     */
    open: async (entryPoint: SchedulingAgentEntryPoint): Promise<boolean> => {
      if (ordersAiPage) {
        agent.openHome(entryPoint);
        return false;
      }

      try {
        const [completion, upcomingTickets] = await Promise.all([fetchOnDemand(completionDataQueryOptions(fleetId)), fetchOnDemand(upcomingTicketsQueryOptions(fleetId))]);
        const singleTicketId = findSingleTicketId(completion?.openTicketsToInstall ?? 0, upcomingTickets);
        if (singleTicketId) {
          agent.openForTicket(singleTicketId, entryPoint);
          return true;
        }
      } catch {
        // Sem a lista, o agente ainda atende pela frota; o clique não pode falhar por isso.
      }
      agent.openForPendingInstalls(entryPoint);
      return false;
    },
  };
};

export default usePendingInstallScheduling;
